// The tenant-facing RBAC permissions: which of the platform's gated capabilities
// this USER holds. Descope mints them into the session token; a role (User,
// Admin, Brand Protection…) is how a person GETS a permission, but code only
// ever asks about permissions — roles are a console-side grouping that can be
// recomposed without touching a line here.
//
// ⚠ These decide what RENDERS. Every one of them is re-checked server-side on
// the routes it guards (see elliot-server `require_permission`) — hiding a
// control is good UI, not access control. Same division of labour as
// `parseCanViewAs` next door in features/viewAs, which reads the staff-plane
// permission (`Impersonate`) through this same parser.
//
// Read from the SESSION token, so a grant changes only when the token does:
// flipping a role in the Descope console does nothing for a session that is
// already open until it refreshes.

/** The permission names, exactly as they are spelled in the Descope console.
 *  Title Case because the project's existing permission (`Impersonate`) is —
 *  renaming one of these means renaming it there too, so don't.
 *
 *  Deliberately small: a permission earns its row only by separating two real
 *  people. API keys and feeds are NOT here — every member may manage them (a
 *  `Manage API Keys`/`Manage Feeds` pair existed briefly, granted to everyone,
 *  and a permission everyone holds blocks nobody; it was deleted rather than
 *  documented). A key can't escalate past this either: keys carry no roles, so
 *  every gated route answers them 403 regardless of who minted them. */
export const PERMISSIONS = {
  /** See the Brand impersonation screen. The only gate — no tenant flag pairs
   *  with it. Recording a decision is the next one. */
  brandImpersonation: 'Brand Impersonation',
  /** Record a decision on a lookalike domain — the screen's Status selector.
   *  Split from seeing the screen because a decision can fire a takedown alert;
   *  the server's decision route demands BOTH this and `brandImpersonation`. */
  brandImpersonationActions: 'Brand Impersonation Actions',
  /** See the Attack surface screen, add assets and categorize them. Every
   *  `/api/attacksurface/*` route is gated server-side on this same name
   *  (`require_permission(ATTACK_SURFACE)`). The only gate, as above. */
  attackSurface: 'Attack Surface',
  /** Change a finding's status: Mark resolved, Accept risk, and their Undo /
   *  Reopen — one server route, so one grant. The route demands BOTH this and
   *  `attackSurface`. */
  attackSurfaceActions: 'Attack Surface Actions',
  /** See the org's token usage + contract, and its Settings section. */
  viewUsage: 'View Usage',
  /** Invite / remove users and assign roles — and see the Members screen. */
  manageUsers: 'Manage Users',
} as const;

export type PermissionName = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Every permission in the session token, wherever Descope filed it.
 *
 * Descope files a role's permissions where the role was GRANTED: project-wide
 * at the top level, tenant-scoped under `tenants[<id>].permissions`. The
 * console's per-user Authorization dialog only offers the tenant kind, so a
 * parser that reads the top level alone silently loses the ordinary way to
 * grant anything (the bug `parseCanViewAs` was born fixing). One set of both,
 * so callers never care which door a grant came through.
 *
 * Only real arrays count — a malformed claim yields fewer permissions, never
 * more (deny by default).
 *
 * Scoped to the tenant the session is ACTING in when the token says which one
 * — Descope's `dct` claim (Descope Current Tenant) carries that tenant's id,
 * and every tenant-derived claim already resolves through it (`account`,
 * `insightsEnabled`, the licence limits). A grant made inside one tenant must
 * not light up a control that reads another tenant's data, and this is the
 * same rule the server enforces (`UserInfo.acting_permissions`).
 *
 * Without `dct` it falls back to the union across every tenant. That is the
 * project's setting today — the user JWT template has the claim switched off,
 * while view-as sessions carry it because the mint passes a tenant explicitly.
 * The union is safe as a fallback here for the reason it is not safe on the
 * server: the worst it can do is draw a control the server then answers 403.
 */
export function parsePermissions(claims: unknown): ReadonlySet<string> {
  const held = new Set<string>();
  const c = (claims ?? {}) as { permissions?: unknown; tenants?: unknown; dct?: unknown };
  collect(c.permissions, held);
  // `tenants` is a plain array of ids when the user holds no roles, hence the
  // shape check.
  if (c.tenants && typeof c.tenants === 'object' && !Array.isArray(c.tenants)) {
    const byTenant = c.tenants as Record<string, unknown>;
    const acting = typeof c.dct === 'string' && c.dct in byTenant ? c.dct : null;
    // A `dct` naming a tenant this token carries no entry for is ignored rather
    // than honoured as "no tenant grants": it can only come from a token we
    // would not trust anyway, and the union below is the safer read.
    const entries = acting ? [byTenant[acting]] : Object.values(byTenant);
    for (const t of entries) {
      collect((t as { permissions?: unknown } | null)?.permissions, held);
    }
  }
  return held;
}

function collect(perms: unknown, into: Set<string>): void {
  if (!Array.isArray(perms)) return;
  for (const p of perms) if (typeof p === 'string' && p) into.add(p);
}
